import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Plane, Ship, ShieldCheck, Star, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { buildWhatsAppUrl } from "@/data/translations";
import { trackEvent } from "@/lib/analytics";
import { HERO_CARD_BG, LOGO_DARK } from "@/data/branding";

const LOGO_BLACK_BG = LOGO_DARK;

const FlagChip = ({ code, label }) => (
  <div className="flex items-center gap-2 rounded-full border border-black/5 bg-white px-3 py-1.5 shadow-sm dark:border-white/10 dark:bg-white/10">
    <img
      src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
      alt={label}
      className="w-5 h-[14px] object-cover rounded-[2px]"
    />
    <span className="text-[12px] font-medium text-[#222] dark:text-white">{label}</span>
  </div>
);

export default function Hero() {
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    trackEvent("home_view", {
      routeName: "home",
      metadata: { source: "hero" },
    });
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#FAFAFA] pt-28 pb-20 md:pt-36 md:pb-28 dark:bg-[#080808]"
    >
      {/* Decorative blurred orange glow */}
      <div className="absolute -top-20 -right-20 h-[440px] w-[440px] rounded-full bg-[#F37021]/16 blur-[120px] pointer-events-none" />
      <div className="absolute top-40 -left-24 h-[320px] w-[320px] rounded-full bg-[#F37021]/8 blur-[110px] pointer-events-none" />

      {/* Flying plane decoration (desktop only — collides with text on mobile) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden hidden lg:block">
        <Plane
          className="w-10 h-10 text-[#F37021] absolute top-[14%] animate-fly"
          strokeWidth={1.5}
        />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 md:px-8 lg:grid-cols-12 lg:gap-10">
        {/* Left column */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#F37021]/25 bg-[#FDF1E8] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#C55810] dark:bg-[#1A1A1A] dark:text-[#F8A056]"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#F37021]" />
            {t.hero.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-[40px] font-bold leading-[1.02] text-[#111] sm:text-6xl md:text-7xl dark:text-white"
            data-testid="hero-title"
          >
            {t.hero.titlePre}
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 text-[#F37021]">{t.hero.titleHighlight}</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 9 Q 50 2, 100 7 T 198 5"
                  stroke="#F37021"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.4"
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 max-w-[42rem] text-lg leading-relaxed text-[#555] md:mt-7 md:text-xl dark:text-[#BAC4D4]"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4"
          >
            <a
              href="#countries"
              onClick={(e) => {
                e.preventDefault();
                trackEvent("shop_and_ship_click", {
                  routeName: "home",
                  metadata: { source: "hero_primary", target: "countries" },
                });
                const el = document.querySelector("#countries");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#F37021] px-7 font-medium text-white shadow-[0_14px_30px_rgba(243,112,33,0.28)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015]"
              data-testid="hero-cta-start-shopping"
            >
              {t.hero.cta1}
              <ArrowRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isRTL ? 'rotate-180 group-hover:-translate-x-1 group-hover:translate-x-0' : ''}`} />
            </a>
            <a
              href="/stock"
              onClick={() => {
                trackEvent("bob_store_click", {
                  routeName: "home",
                  metadata: { source: "hero_store", target: "/stock" },
                });
              }}
              className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-[#111] bg-[#111] px-7 font-medium text-white transition-all hover:-translate-y-0.5 hover:border-[#F37021] hover:bg-[#F37021] sm:min-w-[150px] dark:border-white dark:bg-white dark:text-[#111] dark:hover:border-[#F37021] dark:hover:bg-[#F37021] dark:hover:text-white"
              data-testid="hero-cta-bob-store"
            >
              <ShoppingBag className="h-5 w-5" />
              {t.hero.ctaStore}
            </a>
            <a
              href={buildWhatsAppUrl(t.common.whatsappMsg)}
              onClick={() => {
                trackEvent("whatsapp_click", {
                  routeName: "home",
                  metadata: { source: "hero_secondary" },
                });
                trackEvent("contact_click", {
                  routeName: "home",
                  metadata: { source: "hero_secondary", channel: "whatsapp" },
                });
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-black/12 bg-white px-7 font-medium text-[#111] transition-all hover:-translate-y-0.5 hover:border-[#25D366]/50 hover:text-[#25D366] sm:min-w-[190px] dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#25D366]/50 dark:hover:bg-white/12"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              {t.hero.cta2}
            </a>
          </motion.div>

          {/* Trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <div className="flex items-center gap-4 rounded-2xl border border-black/6 bg-white px-4 py-3 shadow-[0_14px_30px_rgba(16,24,40,0.06)] dark:border-white/10 dark:bg-white/10">
              <div className="flex -space-x-2 rtl:space-x-reverse">
                {["https://i.pravatar.cc/80?img=11", "https://i.pravatar.cc/80?img=32", "https://i.pravatar.cc/80?img=47", "https://i.pravatar.cc/80?img=65"].map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    className="h-9 w-9 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <div>
              <div className="flex items-center gap-0.5">
                {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                  <Star key={k} className="h-4 w-4 fill-[#F37021] text-[#F37021]" />
                ))}
              </div>
              <p className="mt-0.5 text-[13px] text-[#555] dark:text-[#BAC4D4]" data-testid="hero-trust-text">
                {t.hero.trustPill}
              </p>
              </div>
            </div>
          </motion.div>

          {/* Country chips */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-8 flex flex-wrap gap-2.5"
            data-testid="hero-country-chips"
          >
            {t.countries.items.map((c) => (
              <a
                key={c.code}
                href={`#country-${c.code}`}
                onClick={(e) => {
                  e.preventDefault();
                  const target =
                    document.getElementById(`country-${c.code}`) ||
                    document.getElementById("countries");
                  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="flex items-center gap-2 rounded-full border border-black/6 bg-white px-3 py-1.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#F37021]/40 hover:shadow-[0_12px_24px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-white/10"
                data-testid={`hero-chip-${c.code}`}
              >
                <img
                  src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                  alt={c.name}
                  className="w-5 h-[14px] object-cover rounded-[2px]"
                />
                <span className="text-[12px] font-medium text-[#222] dark:text-white">{c.name}</span>
              </a>
            ))}
          </motion.div>
        </div>

        {/* Right column — visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative lg:col-span-5"
        >
          <div className="relative mx-auto aspect-[4/5] max-w-md">
            {/* Glow behind */}
            <div className="absolute inset-0 rounded-[32px] bg-gradient-to-br from-[#F37021]/24 via-[#F37021]/8 to-transparent blur-2xl" />

            {/* Main card with logistics sunset backdrop + group transport visuals */}
            <div className="relative h-full w-full overflow-hidden rounded-[32px] border border-black/10 bg-[#111] grain-overlay shadow-[0_30px_70px_rgba(16,24,40,0.22)]">
              {/* Sunset logistics photograph as the card background */}
              <img
                src={HERO_CARD_BG}
                alt=""
                aria-hidden="true"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              {/* Dark + brand tint overlay so the logo and tagline read crisply */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.78) 100%)",
                }}
              />

              {/* Subtle grid pattern on top */}
              <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>

              {/* Flight arcs */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 500" preserveAspectRatio="none">
                <path
                  d="M 40 400 Q 200 100, 360 200"
                  stroke="#F37021"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5 6"
                  opacity="0.55"
                />
                <path
                  d="M 80 450 Q 150 200, 340 380"
                  stroke="#F37021"
                  strokeWidth="1.5"
                  fill="none"
                  strokeDasharray="3 5"
                  opacity="0.32"
                />
              </svg>

              {/* Logo + tagline block (centered) */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-8 pt-6 pb-32 text-center">
                <img
                  src={LOGO_BLACK_BG}
                  alt="BOB Services"
                  className="h-auto w-[78%] max-w-[300px] drop-shadow-[0_20px_40px_rgba(243,112,33,0.35)]"
                  data-testid="hero-logo-image"
                />
                {/* Tagline under the logo — Land & Sea Shipping */}
                <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#F37021]/30 bg-[#F37021]/15 px-4 py-1.5 backdrop-blur-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F37021]" />
                  <span className="text-[12px] sm:text-[13px] font-semibold tracking-wide text-white uppercase">
                    {t.hero.heroTagline}
                  </span>
                </div>
              </div>

              {/* Shipping mode badges — Air Freight + Sea Freight prominent CTA pills */}
              <div className="absolute bottom-5 left-0 right-0 z-10 flex flex-wrap items-center justify-center gap-2 px-4 sm:bottom-6 sm:gap-2.5 sm:px-5">
                <div
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#F37021] text-white shadow-[0_10px_24px_rgba(243,112,33,0.45)] border border-white/20"
                  data-testid="hero-badge-air"
                >
                  <Plane className="w-3.5 h-3.5" strokeWidth={2.4} />
                  <span className="text-[11px] sm:text-[12px] font-bold tracking-wide uppercase">{t.hero.badgeAir}</span>
                </div>
                <div
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/15 backdrop-blur-md text-white border border-white/30"
                  data-testid="hero-badge-sea"
                >
                  <Ship className="w-3.5 h-3.5 text-[#F37021]" strokeWidth={2.4} />
                  <span className="text-[11px] sm:text-[12px] font-bold tracking-wide uppercase">{t.hero.badgeSea}</span>
                </div>
              </div>

              {/* Secured floating badge — top corner */}
              <div className="absolute top-4 left-4 rtl:right-4 rtl:left-auto bg-white/12 backdrop-blur-md border border-white/20 rounded-2xl px-3 py-2 flex items-center gap-2 z-10">
                <ShieldCheck className="w-4 h-4 text-[#F37021]" />
                <span className="text-[11px] text-white font-medium tracking-wide">{t.hero.badgeSecured}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <div className="relative max-w-7xl mx-auto mt-14 md:mt-24 px-5 md:px-8" data-testid="hero-stats">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[28px] border border-black/8 bg-black/8 shadow-[0_20px_50px_rgba(16,24,40,0.08)] md:grid-cols-4 dark:border-white/10 dark:bg-white/10">
            {t.hero.stats.map((s) => (
            <div key={s.k} className="bg-white p-4 transition-colors hover:bg-[#FDF1E8] sm:p-6 md:p-8 dark:bg-[#121212] dark:hover:bg-[#1B1B1B]">
              <div className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-[#111] dark:text-white">{s.k}</div>
              <div className="mt-1 md:mt-1.5 text-xs sm:text-sm text-[#555] leading-snug dark:text-[#BAC4D4]">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
